/**
 * Roster file parsing for the team step.
 *
 * The upload bar accepts a real CSV roster (the "download a sample" link emits
 * the canonical shape: First name, Last name, Sex, Home ZIP, Date of birth) and
 * turns it into editable `TeamMember` cards. This is genuine parsing — the file
 * the employer drops in is what populates the list; nothing is fabricated.
 *
 * Excel (.xls/.xlsx) is a binary format we don't parse in the browser; those
 * files are rejected with a clear message pointing at CSV, rather than silently
 * loading the wrong data.
 */

import { formatZip, makeTeamMember } from "./data";
import type { Sex, TeamMember } from "./types";

/** Thrown with a user-facing message when a file can't be turned into a roster. */
export class RosterParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RosterParseError";
  }
}

const MAX_BYTES = 10 * 1024 * 1024; // 10MB — matches the dropzone copy.

/** Column synonyms we recognize in a header row (normalized: lowercased, single-spaced). */
const HEADER_SYNONYMS: Record<keyof RosterColumns, readonly string[]> = {
  firstName: ["first name", "firstname", "first", "given name", "fname"],
  lastName: ["last name", "lastname", "last", "surname", "family name", "lname"],
  sex: ["sex", "gender"],
  zip: ["home zip", "zip", "zip code", "zipcode", "postal code", "postal", "home zip code"],
  dob: ["date of birth", "dob", "birth date", "birthdate", "birthday", "d.o.b."],
};

/** Human-facing column names, used in the "missing columns" rejection message. */
const COLUMN_LABELS: Record<keyof RosterColumns, string> = {
  firstName: "First name",
  lastName: "Last name",
  sex: "Sex",
  zip: "Home ZIP",
  dob: "Date of birth",
};

const COLUMN_KEYS = Object.keys(HEADER_SYNONYMS) as (keyof RosterColumns)[];

interface RosterColumns {
  firstName: number;
  lastName: number;
  sex: number;
  zip: number;
  dob: number;
}

/** Absent-column sentinel: the field wasn't found in the header. */
const NONE = -1;

/**
 * Parse an uploaded roster file into editable team members. Resolves to one
 * member per data row; throws `RosterParseError` (with a friendly message) when
 * the file is empty, too large, not a CSV, or has no usable rows.
 */
export async function parseRosterFile(file: File): Promise<TeamMember[]> {
  if (file.size === 0) {
    throw new RosterParseError("That file looks empty — pick a roster with at least one person.");
  }
  if (file.size > MAX_BYTES) {
    throw new RosterParseError("That file is over 10MB. Trim it down or upload a CSV export.");
  }

  const text = await file.text();
  if (looksBinary(text, file.name)) {
    throw new RosterParseError(
      "Excel files aren't supported yet — re-save as CSV (or use the sample) and try again.",
    );
  }

  const rows = parseCsvRows(text).filter((cells) => cells.some((c) => c.trim().length > 0));
  if (rows.length === 0) {
    throw new RosterParseError("We couldn't find any rows in that file.");
  }

  const { columns, dataRows } = resolveColumns(rows);

  const members: TeamMember[] = [];
  for (const cells of dataRows) {
    const firstName = cell(cells, columns.firstName);
    const lastName = cell(cells, columns.lastName);
    // Skip rows with no name at all — usually trailing blanks or notes.
    if (!firstName && !lastName) continue;
    members.push(
      makeTeamMember({
        firstName,
        lastName,
        sex: normalizeSex(cell(cells, columns.sex)),
        zip: formatZip(cell(cells, columns.zip)),
        dateOfBirth: normalizeDob(cell(cells, columns.dob)),
      }),
    );
  }

  if (members.length === 0) {
    throw new RosterParseError(
      "We couldn't read any people from that file. Check the columns, or download our sample.",
    );
  }

  return members;
}

/** Heuristic: a parsed-as-text spreadsheet (PK zip / OLE) won't be valid CSV. */
function looksBinary(text: string, fileName: string): boolean {
  if (/\.(xlsx|xls)$/i.test(fileName)) return true;
  // XLSX is a zip ("PK"), legacy XLS is an OLE compound file (starts with 0xD0 0xCF).
  return text.startsWith("PK\u0003\u0004") || text.charCodeAt(0) === 0xd0;
}

/**
 * Minimal RFC-4180-ish CSV reader: handles quoted fields, escaped quotes (""),
 * embedded commas/newlines inside quotes, a leading BOM, and CRLF/CR endings.
 */
function parseCsvRows(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  row.push(field);
  rows.push(row);
  return rows;
}

/** Loose header form for matching: lowercase, strip everything but a-z0-9. */
function loosen(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Pre-computed loose synonym forms per column, for exact + fuzzy matching. */
const LOOSE_SYNONYMS: Record<keyof RosterColumns, readonly string[]> = {
  firstName: HEADER_SYNONYMS.firstName.map(loosen),
  lastName: HEADER_SYNONYMS.lastName.map(loosen),
  sex: HEADER_SYNONYMS.sex.map(loosen),
  zip: HEADER_SYNONYMS.zip.map(loosen),
  dob: HEADER_SYNONYMS.dob.map(loosen),
};

/** Classic Levenshtein edit distance (rolling two-row buffer). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1).fill(0);
  for (let i = 0; i < a.length; i += 1) {
    curr[0] = i + 1;
    for (let j = 0; j < b.length; j += 1) {
      const cost = a[i] === b[j] ? 0 : 1;
      const deletion = (curr[j] ?? 0) + 1;
      const insertion = (prev[j + 1] ?? 0) + 1;
      const substitution = (prev[j] ?? 0) + cost;
      curr[j + 1] = Math.min(deletion, insertion, substitution);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length] ?? 0;
}

/**
 * Match one header cell to a column key, tolerating close names and typos
 * (e.g. `Zipcode`, `Birthdate`, `Frist Name`). Exact loose match wins; otherwise
 * the closest column within a length-scaled edit-distance budget is used.
 * Returns `null` when the header resembles no expected column.
 */
function matchColumn(raw: string): keyof RosterColumns | null {
  const token = loosen(raw);
  if (!token) return null;
  // Budget: 1 edit for short headers, 2 for longer ones. Keeps unrelated
  // columns (email, department, salary) from matching by accident.
  const budget = token.length <= 6 ? 1 : 2;
  let best: { key: keyof RosterColumns; distance: number } | null = null;
  for (const key of COLUMN_KEYS) {
    for (const synonym of LOOSE_SYNONYMS[key]) {
      const distance = token === synonym ? 0 : levenshtein(token, synonym);
      if (distance > budget) continue;
      if (!best || distance < best.distance) best = { key, distance };
      if (distance === 0) return key;
    }
  }
  return best?.key ?? null;
}

/**
 * Map the header row to column indices via fuzzy matching. The first row is
 * always treated as the header. Rejects the file when it contains none of the
 * expected columns; otherwise maps whatever it recognizes (unrecognized fields
 * stay `NONE` and are completed later by the required-field cards).
 */
function resolveColumns(rows: string[][]): { columns: RosterColumns; dataRows: string[][] } {
  const [header = [], ...dataRows] = rows;
  const columns: RosterColumns = {
    firstName: NONE,
    lastName: NONE,
    sex: NONE,
    zip: NONE,
    dob: NONE,
  };

  header.forEach((raw, index) => {
    const key = matchColumn(raw);
    if (key && columns[key] === NONE) columns[key] = index;
  });

  const recognized = COLUMN_KEYS.filter((key) => columns[key] !== NONE);
  if (recognized.length === 0) {
    const expected = COLUMN_KEYS.map((key) => COLUMN_LABELS[key]).join(", ");
    throw new RosterParseError(
      `We couldn't find any of the expected columns (${expected}) in that file. Rename your columns to match these, or download our sample.`,
    );
  }

  return { columns, dataRows };
}

function cell(cells: string[], index: number): string {
  if (index < 0) return "";
  return (cells[index] ?? "").trim();
}

function normalizeSex(raw: string): Sex | "" {
  const t = raw.trim().toLowerCase();
  if (["f", "female", "woman", "w"].includes(t)) return "female";
  if (["m", "male", "man"].includes(t)) return "male";
  return "";
}

/**
 * Normalize a date of birth to YYYY-MM-DD. Accepts ISO (passed through) and
 * common numeric US formats (M/D/YYYY, M-D-YY, etc.). Returns "" when the value
 * isn't a recognizable date — the card then shows an empty, fixable field.
 */
function normalizeDob(raw: string): string {
  const value = raw.trim();
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const m = value.match(/^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})$/);
  if (!m) return "";
  const [, a = "", b = "", c = ""] = m;

  let year: number;
  let month: number;
  let day: number;
  if (a.length === 4) {
    // YYYY-M-D
    year = Number(a);
    month = Number(b);
    day = Number(c);
  } else {
    // M/D/YYYY (US). Two-digit years map to 1900s/2000s around a 1930 pivot.
    month = Number(a);
    day = Number(b);
    year = c.length <= 2 ? (Number(c) > 30 ? 1900 + Number(c) : 2000 + Number(c)) : Number(c);
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  const iso = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return iso;
}
